import { handleResponse, notify } from 'src/store/vue-axios/interceptorHelper.js'
import { i18n } from 'src/config/i18n.ts'
import GlobalPrototypes from 'src/config/services/globalPrototypes.ts'
import { KeycloakAuth } from 'src/config/auth/KeycloakAuth.ts'
import { wasStepUpReturnedRecently } from 'src/router/middleware/authentification/recentAuth.js'

let blockErrorNotification = false

const handleServerNotAvailable = (error) => {
  const showBrokenConnectionMessage = () => {
    GlobalPrototypes.toastNotify({
      type: 'error',
      text: i18n.global.t('chats.message.fetching_problem')
    })

    blockErrorNotification = true
    setTimeout(() => {
      // keep some delay to avoid spamming users with too many messages
      blockErrorNotification = false
    }, 2000)
  }

  const isGetRequest = error.config.method === 'get'
  const hasStatusMessage = error.config.showMessages
  const showErrorMessage = !isGetRequest && !blockErrorNotification && hasStatusMessage

  // error message should be displayed only for non get method requests that have showMessages prop true
  showErrorMessage && showBrokenConnectionMessage()

  return Promise.reject(error)
}

const handle2faRateLimit = (error) => {
  const BASE_DELAY = 500

  const retryDelay = (attempt) => {
    return BASE_DELAY * Math.pow(2, 5 - attempt)
  }

  if (!error.config || !error.config.retry || error.config.retry === 0) {
    return Promise.reject(error)
  }

  error.config.retry -= 1

  const delayRetryRequest =
    new Promise((resolve) => {
      setTimeout(() => {
        resolve()
      }, retryDelay(error.config.retry + 1))
    })
  return delayRetryRequest.then(() => GlobalPrototypes.axios(error.config))
}

const handleOthers = (error) => {
  handleResponse(error.response.data.meta)
  notify(error.response.config)

  return Promise.reject(error)
}

const STEP_UP_REQUIRED_CODE = 'step_up_required'

const isStepUpRequired = (error) => error?.response?.status === 401 && error.response.data?.data?.code === STEP_UP_REQUIRED_CODE

let pendingStepUpRedirect = null

const redirectToStepUp = async () => {
  // Just came back from a step-up login and the API still demands one: do not loop
  if (wasStepUpReturnedRecently()) return

  const realm = await KeycloakAuth.getStoredRealm()
  const kcAuth = realm ? await KeycloakAuth.getInstance(realm) : null
  if (!kcAuth) return

  const { pathname, search, hash } = window.location
  await kcAuth.signinRedirectStepUp(`${pathname}${search}${hash}`)
}

/**
 * The backend answers 401 `step_up_required` when the Keycloak session has acr < 2. Refreshing the token would not
 * help, so redirect to Keycloak for a step-up login and return to the current page afterwards.
 * Parallel failing requests share one redirect. Within a minute after returning from a step-up login there is no
 * redirect (loop guard): the token did not get acr 2, so repeating the login would not help.
 */
const handleStepUpRequired = async (error) => {
  if (!pendingStepUpRedirect) {
    pendingStepUpRedirect = redirectToStepUp()
  }

  try {
    await pendingStepUpRedirect
  } finally {
    pendingStepUpRedirect = null
  }

  return Promise.reject(error)
}

export { handleServerNotAvailable, handle2faRateLimit, handleOthers, isStepUpRequired, handleStepUpRequired }
